import { useState } from "react";
import { Check } from "lucide-react";
import { api } from "../lib/api";
import { FUNDING_TYPES } from "../data/content";
import { toast } from "sonner";
import { CompanyLookup } from "./CompanyLookup";

export function QuoteForm({ source = "contact", presetType = "", submitLabel = "Request Funding", successText = "Your funding enquiry has been received. A DNA Finance adviser will contact you shortly." }) {
  const [form, setForm] = useState({ name: "", business: "", company_number: "", phone: "", email: "", funding_amount: "", funding_type: presetType, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [agree, setAgree] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error("Please confirm you consent to be contacted."); return; }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source });
      setDone(true);
      toast.success("Enquiry received. An adviser will be in touch shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="border border-lime/40 bg-lime/5 p-12 text-center h-full flex flex-col items-center justify-center">
        <div className="w-16 h-16 bg-lime flex items-center justify-center mb-6"><Check className="w-8 h-8 text-[#1A1A1A]" /></div>
        <h3 className="text-2xl font-bold">Thank you, {form.name.split(" ")[0]}</h3>
        <p className="text-grey mt-3 max-w-sm">{successText}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="border border-[#808080]/30 p-8 md:p-10 space-y-5 bg-[#1A1A1A]">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Name" required><input data-testid="contact-name" required value={form.name} onChange={set("name")} className="field" placeholder="Your full name" /></Field>
        <Field label="Business"><CompanyLookup value={form.business} onChange={(v) => setForm((f) => ({ ...f, business: v }))} onSelect={(c) => setForm((f) => ({ ...f, business: c.title, company_number: c.company_number }))} /></Field>
        <Field label="Company number"><input data-testid="contact-company-number" value={form.company_number} onChange={set("company_number")} className="field" placeholder="Auto-fills when you pick a company" /></Field>
        <Field label="Phone" required><input data-testid="contact-phone" required value={form.phone} onChange={set("phone")} className="field" placeholder="Phone number" /></Field>
        <Field label="Email" required><input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} className="field" placeholder="you@company.co.uk" /></Field>
        <Field label="Funding required"><input data-testid="contact-amount" value={form.funding_amount} onChange={set("funding_amount")} className="field" placeholder="e.g. £250,000" /></Field>
        <Field label="Funding type" className="sm:col-span-2">
          <select data-testid="contact-type" value={form.funding_type} onChange={set("funding_type")} className="field">
            <option value="">Select type</option>
            {FUNDING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Message"><textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={4} className="field resize-none" placeholder="Tell us about your funding requirement" /></Field>
      <label className="flex items-start gap-3 text-sm text-grey cursor-pointer">
        <input data-testid="contact-consent" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-[#D8FF00] w-4 h-4" />
        <span>I consent to DNA Finance contacting me about my enquiry. We handle your data in line with our privacy policy and AML obligations.</span>
      </label>
      <button data-testid="contact-submit" disabled={loading} className="w-full bg-lime text-[#1A1A1A] font-bold py-4 hover:glow-lime transition-shadow">{loading ? "Sending..." : submitLabel}</button>
      <style>{`.field{width:100%;background:#111;border:1px solid rgba(128,128,128,.3);padding:.75rem 1rem;color:#fff;outline:none}.field:focus{border-color:#D8FF00}`}</style>
    </form>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-wide text-grey mb-2">{label}{required && <span className="text-lime"> *</span>}</label>
      {children}
    </div>
  );
}
